import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCustomSearchParams, useWidgetHelp, useCustomTimer, useChangeEffect } from 'src/hook';

import { createPaymentAddress } from 'src/thunk/payment';
import { createQuote } from 'src/thunk/quote';
import { getMarketPrice } from 'src/thunk/marketPrice';

import { useAppContext } from 'src/context/useAppContext';

import { resetQuote } from 'src/store/slice/quote/slice';
import { selectCreatedQuote } from 'src/store/slice/quote/state';
import { selectPayment } from 'src/store/slice/payment/state';
import { selectMarketPrice, selectStatusGetMarketPrice } from 'src/store/slice/marketPrice/state';
import { resetMarketPrice } from 'src/store/slice/marketPrice/slice';
import { ERequestStatus } from 'src/api/enumRequest';

const useDefaultInvoice = () => {
  const dispatch: any = useDispatch();

  const { isDemo } = useCustomSearchParams();
  const { isLongLivedQuote, isConvertOnArrival, isPaymentCompleted, isSetedDefaultInvoiceCurrency } = useWidgetHelp();
  const { currency } = useAppContext();

  const createdQuote = useSelector(selectCreatedQuote);
  const marketPrice = useSelector(selectMarketPrice);
  const payment = useSelector(selectPayment);
  const statusGetMarketPrice = useSelector(selectStatusGetMarketPrice);

  const { id: paymentId, currency_id: paymentCurrencyId, target_currency_amount_left } = payment || {};
  const { currency_id: selectedCurrencyId, network: selectedCurrencyNetwork } = currency;

  /**
   * useEffect for send request to create payment address if the selected currency matches the invoice currency.
   */
  useChangeEffect(() => {
    if (payment && isSetedDefaultInvoiceCurrency && !isPaymentCompleted && !isDemo) {
      isLongLivedQuote && dispatch(resetQuote());
      isConvertOnArrival && dispatch(resetMarketPrice());

      dispatch(
        createPaymentAddress({
          payment_id: paymentId,
          currency: selectedCurrencyId,
          currency_network: selectedCurrencyNetwork,
        })
      );
    }
  }, [payment, currency]);

  /**
   * useEffect for send request to create quote if pricing_mode is LongLivedQuote and selected currency is different from the invoice currency
   */
  useChangeEffect(() => {
    if (payment && !isSetedDefaultInvoiceCurrency && isLongLivedQuote && !isPaymentCompleted && !isDemo) {
      dispatch(
        createQuote({
          payment_id: paymentId,
          currency_id: selectedCurrencyId,
          network: selectedCurrencyNetwork,
        })
      );
    }
  }, [payment, currency]);

  /**
   * useEffect for send request to create payment address after request create quote and if pricing_mode is LongLivedQuote
   */
  useEffect(() => {
    if (payment && !isSetedDefaultInvoiceCurrency && isLongLivedQuote && createdQuote && !isDemo) {
      dispatch(
        createPaymentAddress({
          payment_id: paymentId,
          currency: selectedCurrencyId,
          currency_network: selectedCurrencyNetwork,
          quote_id: createdQuote.quote_id,
        })
      );
    }
  }, [createdQuote]);

  /**
   * useEffect for send request to get market currency if pricing_mode is ConvertOnArrival and selected currency is different from the invoice currency
   */
  useChangeEffect(() => {
    if (payment && !isSetedDefaultInvoiceCurrency && isConvertOnArrival && !isPaymentCompleted && !isDemo) {
      dispatch(
        getMarketPrice({
          paymentId,
          sourceCurrencyId: paymentCurrencyId,
          targetCurrencyId: selectedCurrencyId,
          sourceCurrencyAmount: target_currency_amount_left,
        })
      );
    }
  }, [payment, currency]);

  /**
   * useEffect for send request to create payment address after request get market price and if pricing_mode is isConvertOnArrival
   */
  useEffect(() => {
    if (payment && statusGetMarketPrice === ERequestStatus.success && isConvertOnArrival && !isDemo) {
      dispatch(
        createPaymentAddress({
          payment_id: paymentId,
          currency: selectedCurrencyId,
          currency_network: selectedCurrencyNetwork,
        })
      );
    }
  }, [statusGetMarketPrice]);

  useCustomTimer(() => {
    if (payment && isLongLivedQuote) {
      dispatch(
        createQuote({
          payment_id: paymentId,
          currency_id: selectedCurrencyId,
          network: selectedCurrencyNetwork,
        })
      );
    }
  }, [payment, createdQuote]);

  useCustomTimer(() => {
    if (payment && isConvertOnArrival) {
      dispatch(
        getMarketPrice({
          paymentId,
          sourceCurrencyId: paymentCurrencyId,
          targetCurrencyId: selectedCurrencyId,
          sourceCurrencyAmount: target_currency_amount_left,
        })
      );
    }
  }, [payment, marketPrice]);
};

export { useDefaultInvoice };
